import { useState, useCallback } from 'react';
import { Admin, Batch, Student } from '../types/batch.types';
import { ApiService } from '../../api/api-service';
import { showNotification } from '@mantine/notifications';
import { ComboboxItem } from '@mantine/core';

export const useBatchManagement = () => {
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [batchNumbersDropdown, setBatchNumbersDropdown] = useState<string[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [adminDropdown, setAdminDropdown] = useState<ComboboxItem[]>([]);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ApiService.getInstance().get("/batches");
      const sortedBatches = response.data.data
        .map((batch: Batch) => ({
          ...batch,
          linkedBatches: batch.linkedBatches || [],
        }))
        .sort((a: Batch, b: Batch) => {
          const numA = parseInt(a.batch_no?.trim(), 10);
          const numB = parseInt(b.batch_no?.trim(), 10);
          // Both numeric: descending sort
          if (!isNaN(numA) && !isNaN(numB)) return numB - numA;
          // Push non-numeric batch_no to end
          if (isNaN(numA)) return 1;
          if (isNaN(numB)) return -1;
          return 0;
        });
      setBatches(sortedBatches);

      const batchNos = sortedBatches.map((batch: Batch) => batch.batch_no);
      setBatchNumbersDropdown(batchNos);
      // console.log(batchNos, "Check the Batch Number Data");

    } catch (error) {
      console.error("Error fetching batches:", error);
      showNotification({
        title: "Error",
        message: "Failed to fetch batches",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, []);
  const fetchAdmins = async () => {
    try {
      const response = await ApiService.getInstance().get("/admin");
      return response.data.admins;
    } catch (error) {
      console.error("Error fetching admins:", error);
      return [];
    }
  };

  // Add this function after existing functions
  const fetchAdminsData = useCallback(async () => {
    try {
      const adminData = await fetchAdmins();
      setAdmins(adminData);

      const adminOptions: ComboboxItem[] = adminData
        .map((admin: any) => {
          // Handle both clean format { firstName: "X" } and raw DynamoDB { firstName: { S: "X" } }
          const first = (typeof admin.firstName === 'string' ? admin.firstName : admin.firstName?.S) || '';
          const last = (typeof admin.lastName === 'string' ? admin.lastName : admin.lastName?.S) || '';
          const name = `${first} ${last}`.trim();
          return { label: name, value: name };
        })
        .filter((opt: ComboboxItem) => opt.value); // Remove any empty values
      setAdminDropdown(adminOptions);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  }, []);
  const createBatch = useCallback(async (batchData: Partial<Batch>) => {
    try {
      setLoading(true);
      await ApiService.getInstance().post("/batches", batchData);

      // If we have linked batches, link them
      if (batchData.linkedBatches && batchData.linkedBatches.length > 0) {
        await linkBatches(batchData.batch_no!, batchData.linkedBatches);
      }
      showNotification({
        title: "Success",
        message: `Batch #${batchData.batch_no} created successfully`,
        color: "green",
      });
      await fetchBatches();

      return true;
    } catch (error) {
      console.error("Error creating batch:", error);
      showNotification({
        title: "Error",
        message: "Failed to create batch",
        color: "red",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchBatches]);

  const updateBatch = useCallback(async (batchNo: string, batchData: Partial<Batch>) => {
    try {
      setLoading(true);
      await ApiService.getInstance().put(`/batches/${batchNo}`, batchData);

      // If we have linked batches, link them
      if (batchData.linkedBatches && batchData.linkedBatches.length > 0) {
        await linkBatches(batchNo, batchData.linkedBatches);
      }
      showNotification({
        title: "Success",
        message: `Batch #${batchNo} updated successfully`,
        color: "green",
      });
      await fetchBatches();
      return true;
    } catch (error) {
      console.error("Error updating batch:", error);
      showNotification({
        title: "Error",
        message: "Failed to update batch",
        color: "red",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchBatches]);

  const deleteBatch = useCallback(async (batchNo: string) => {
    try {
      setLoading(true);
      await ApiService.getInstance().delete(`/batches/${batchNo}`);
      showNotification({
        title: "Success",
        message: `Batch #${batchNo} deleted successfully`,
        color: "green",
      });
      await fetchBatches();
      return true;
    } catch (error) {
      console.error("Error deleting batch:", error);
      showNotification({
        title: "Error",
        message: "Failed to delete batch",
        color: "red",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchBatches]);

  const updateBatchStatus = useCallback(async (batchNo: string, status: string, statusComment?: string) => {
    try {
      setLoading(true);
      await ApiService.getInstance().put("/batches/update-status", { batch_no: batchNo, batchStatus: status, statusComment });
      showNotification({
        title: "Success",
        message: `Batch #${batchNo} status updated to ${status}`,
        color: "green",
      });
      await fetchBatches();
      return true;
    } catch (error) {
      console.error("Error updating batch status:", error);
      showNotification({
        title: "Error",
        message: (error as any)?.response?.data?.error || "Failed to update batch status",
        color: "red",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchBatches]);

  // Enhanced link batches function
  const linkBatches = useCallback(async (sourceBatchNo: string, linkedBatchNos: string[]) => {
    try {
      setLoading(true);
      const response = await ApiService.getInstance().post("/batches/link", {
        batchNos: [sourceBatchNo, ...linkedBatchNos]
      });

      // Show detailed success message with merge information
      if (response.data.mergedData) {
        const { totalStudents, totalInstructors, linkedBatches } = response.data.mergedData;
        showNotification({
          title: "Batches Merged Successfully",
          message: `${linkedBatches.length} batches linked with ${totalStudents} students and ${totalInstructors}
           merged`,
          color: "green",
          autoClose: 5000,
        });
      } else {
        showNotification({
          title: "Success",
          message: "Batches linked successfully",
          color: "green",
        });
      }

      await fetchBatches();
      return true;
    } catch (error) {
      console.error("Error linking batches:", error);
      showNotification({
        title: "Error",
        message: "Failed to link batches",
        color: "red",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchBatches]);


  // Add this function after linkBatches
  const unlinkBatches = useCallback(async (batchNos: string[]) => {
    try {
      setLoading(true);
      const response = await ApiService.getInstance().post("/batches/unlink", {
        batchNos: batchNos
      });

      showNotification({
        title: "Success",
        message: `${batchNos.length} batch${batchNos.length > 1 ? 'es' : ''} unlinked successfully`,
        color: "green",
        autoClose: 3000,
      });

      await fetchBatches();
      return true;
    } catch (error) {
      console.error("Error unlinking batches:", error);
      showNotification({
        title: "Error",
        message: "Failed to unlink batches",
        color: "red",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchBatches]);
  // Enhanced function to get merge preview
  const getMergePreview = useCallback(async (batchNos: string[]) => {
    if (batchNos.length < 2) return null;

    try {
      const selectedBatches = batches.filter(batch => batchNos.includes(batch.batch_no));

      // Collect all students
      const allStudents: any[] = [];
      const allInstructors: any[] = [];

      selectedBatches.forEach(batch => {
        if (batch.students) {
          allStudents.push(...batch.students);
        }
        if (batch.instructor) {
          allInstructors.push(...batch.instructor);
        }
      });

      // Get unique students and instructors
      const uniqueStudents = allStudents.reduce((acc, student) => {
        const exists = acc.find((s: any) => s.email === student.email);
        if (!exists) acc.push(student);
        return acc;
      }, []);

      const uniqueInstructors = allInstructors.reduce((acc, instructor) => {
        const exists = acc.find((i: any) => i.email === instructor.email);
        if (!exists) {
          acc.push(instructor);
        } else {
          // Show that this instructor will be merged
          exists._willBeMerged = true;
        }
        return acc;
      }, []);

      return {
        totalBatches: batchNos.length,
        totalStudents: uniqueStudents.length,
        totalInstructors: uniqueInstructors.length,
        duplicateInstructors: allInstructors.length - uniqueInstructors.length,
        duplicateStudents: allStudents.length - uniqueStudents.length,
        previewStudents: uniqueStudents.slice(0, 5), // Show first 5 for preview
        previewInstructors: uniqueInstructors.slice(0, 5), // Show first 5 for preview
      };
    } catch (error) {
      console.error("Error generating merge preview:", error);
      return null;
    }
  }, [batches]);

  const getFilteredBatchOptions = useCallback((currentBatchNo: string) => {
    return batchNumbersDropdown.filter(batchNo => batchNo !== currentBatchNo);
  }, [batchNumbersDropdown]);

  const getBatchByBatchNo = useCallback((batchNo: string) => {
    return batches.find(batch => batch.batch_no === batchNo) || null;
  }, [batches]);

  // Helper function to check if batches are linked
  const areLinked = useCallback((batchNo1: string, batchNo2: string) => {
    const batch1 = getBatchByBatchNo(batchNo1);
    return batch1?.linkedBatches?.includes(batchNo2) || false;
  }, [getBatchByBatchNo]);

  // Get all batches in a linked group
  const getLinkedGroup = useCallback((batchNo: string) => {
    const batch = getBatchByBatchNo(batchNo);
    if (!batch?.linkedBatches?.length) return [batchNo];

    return [batchNo, ...batch.linkedBatches];
  }, [getBatchByBatchNo]);

  return {
    loading,
    setLoading,
    batches,
    fetchBatches,
    admins,
    adminDropdown,
    fetchAdminsData,
    createBatch,
    updateBatch,
    deleteBatch,
    updateBatchStatus,
    linkBatches,
    unlinkBatches,
    getMergePreview,
    getFilteredBatchOptions,
    getBatchByBatchNo,
    areLinked,
    getLinkedGroup
  };
};